import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authService from '../../services/auth';
import { buildEndpointMap } from '../../utils/rbac';

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await authService.login(username, password);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const secureLoginUser = createAsyncThunk(
  'auth/secureLoginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await authService.secureLogin(username, password);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refreshUserToken = createAsyncThunk(
  'auth/refreshUserToken',
  async (_, { rejectWithValue }) => {
    try {
      const newToken = await authService.refreshToken();
      return newToken;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const invalidateUserToken = createAsyncThunk(
  'auth/invalidateUserToken',
  async (_, { rejectWithValue }) => {
    try {
      await authService.invalidateToken();
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      return user;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  token: localStorage.getItem('token'),
  refresh_token: localStorage.getItem('refresh_token'),
  user: null,
  isLoggedIn: !!localStorage.getItem('token'),
  loading: false,
  error: null,
  endpointMap: {},
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      if (action.payload?.allowed_endpoints) {
        state.endpointMap = buildEndpointMap(action.payload.allowed_endpoints);
      }
    },
    forceLogout: (state) => {
      // Force logout without API call (for immediate logout)
      state.token = null;
      state.refresh_token = null;
      state.user = null;
      state.isLoggedIn = false;
      state.endpointMap = {};
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('loginLockout');
    },
    updateToken: (state, action) => {
      state.token = action.payload;
      state.isLoggedIn = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refresh_token = action.payload.refresh_token;
        state.user = action.payload.user;
        state.isLoggedIn = true;
        state.endpointMap = buildEndpointMap(action.payload.user?.allowed_endpoints || []);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Secure Login
      .addCase(secureLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(secureLoginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refresh_token = action.payload.refresh_token;
        state.user = action.payload.user;
        state.isLoggedIn = true;
        state.endpointMap = buildEndpointMap(action.payload.user?.allowed_endpoints || []);
      })
      .addCase(secureLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.token = null;
        state.refresh_token = null;
        state.user = null;
        state.isLoggedIn = false;
        state.endpointMap = {};
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout fails, clear the state
        state.token = null;
        state.refresh_token = null;
        state.user = null;
        state.isLoggedIn = false;
        state.endpointMap = {};
      })
      // Refresh token
      .addCase(refreshUserToken.fulfilled, (state, action) => {
        state.token = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(refreshUserToken.rejected, (state) => {
        // If refresh fails, clear the state
        state.token = null;
        state.refresh_token = null;
        state.user = null;
        state.isLoggedIn = false;
        state.endpointMap = {};
      })
      // Invalidate token
      .addCase(invalidateUserToken.fulfilled, (state) => {
        state.token = null;
        state.refresh_token = null;
        state.user = null;
        state.isLoggedIn = false;
        state.endpointMap = {};
      })
      .addCase(invalidateUserToken.rejected, (state) => {
        // Even if invalidation fails, clear the state
        state.token = null;
        state.refresh_token = null;
        state.user = null;
        state.isLoggedIn = false;
        state.endpointMap = {};
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.endpointMap = buildEndpointMap(action.payload?.allowed_endpoints || []);
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, updateUser, forceLogout, updateToken } = authSlice.actions;
export default authSlice.reducer; 